import styled from "@emotion/styled";
import { Collapse, Tooltip } from "@mui/material";
import classNames from "classnames";
import { useRef, useState, type HTMLAttributes, type PropsWithChildren, type ReactNode } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { IconButton } from "../buttons/Buttons";
import { ContextMenuIconButton } from "../popup/ContextMenu";
import {
  FONT_WEIGHT_SEMI_BOLD,
  PROP_PANEL_GUTTER,
  PROP_PANEL_PADDING,
  PROPERTY_PANEL_SECTION_CLASSNAME,
  transientOptions,
} from "../utilities";

const headingHeight = 30;
const verticalSpacing = "10px";
const nestedClassName = "nested";
const emptyClassName = "empty";
const highlightClassName = "highlight";

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  height: ${headingHeight}px;

  margin-block-end: ${verticalSpacing};

  > h3 {
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.primaryStrong};

    font-size: ${({ theme }) => theme.fontSize};
    font-weight: ${FONT_WEIGHT_SEMI_BOLD};
    line-height: 16px;

    margin: 0;
    padding: 0;

    user-select: none;
  }
`;

const HeadingControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;

  ${IconButton},
  ${ContextMenuIconButton} {
    font-size: 14px;
  }
`;

const ExpandButton = styled(IconButton)`
  font-size: 12px;
`;

const PropertiesPanelSectionContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.outlineSoft};
  margin: 0 -${PROP_PANEL_GUTTER}px ${verticalSpacing};
  padding: 0 ${PROP_PANEL_PADDING}px ${verticalSpacing};

  // Collapsed section, or when there is no content.
  &.${emptyClassName} {
    ${Heading} {
      margin-block-end: 0;
    }
  }

  // Highlighted section, used for noting overrides etc.
  &.${highlightClassName} {
    ${Heading} > h3 {
      position: relative;
      color: ${({ theme }) => theme.secondaryStrong};
      &:before {
        content: "";
        position: absolute;
        left: -${PROP_PANEL_PADDING}px;
        top: 0;
        width: 3px;
        height: 100%;
        border-radius: 0 2px 2px 0;
        background: ${({ theme }) => theme.secondaryStrong};
      }
    }
    ${HeadingControls} {
      color: ${({ theme }) => theme.secondaryStrong};
    }
  }

  // Nested sections.
  & &,
  &.${nestedClassName} {
    ${Heading} > h3 {
      color: inherit;
      &:before {
        content: none; // Remove the highlight line for nested sections.
      }
    }

    ${HeadingControls} {
      color: inherit;
    }

    border-bottom: 0;
    margin-inline: -${PROP_PANEL_PADDING - PROP_PANEL_GUTTER}px; // negative margin to counteract the padding.
  }
`;

const H3 = styled("h3", transientOptions)<{ $hasHelperText: boolean }>`
  ${({ $hasHelperText, theme }) =>
    $hasHelperText &&
    `
    &:hover {
      color: ${theme.generalTextIcon};
    }
  `}
`;

type PanelProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title?: ReactNode;
  helperText?: string;
  empty?: boolean;
  controls?: ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  headingProps?: HTMLAttributes<HTMLDivElement>;
  highlightHeading?: boolean;
  nested?: boolean;
};

export const PropertiesPanelSection = ({
  title,
  helperText,
  empty,
  controls,
  collapsible,
  defaultCollapsed,
  headingProps,
  highlightHeading,
  nested,
  children,
  ...htmlAttributes
}: PropsWithChildren<PanelProps>) => {
  const formatMessage = useFormatMessage(formatStrings);

  const headingRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  const handleHeadingClick = () => {
    if (collapsible) {
      setExpanded((prev) => !prev);
    }
  };

  return (
    <PropertiesPanelSectionContainer
      data-test="properties-panel-section"
      {...htmlAttributes}
      className={classNames(
        {
          [nestedClassName]: !!nested, // Using classnames instead of vars to avoid interpolation issues.
          [emptyClassName]: !!empty,
          [highlightClassName]: !!highlightHeading,
          [PROPERTY_PANEL_SECTION_CLASSNAME]: true,
        },
        htmlAttributes.className,
      )}
    >
      <Collapse in={expanded} collapsedSize={headingHeight}>
        <Heading ref={headingRef} onClick={handleHeadingClick} {...headingProps}>
          {typeof title === "string" || title === undefined ? (
            <Tooltip title={helperText} placement="top" disableHoverListener={!helperText}>
              <H3 $hasHelperText={!!helperText}>{title ?? formatMessage("title")}</H3>
            </Tooltip>
          ) : (
            title
          )}

          {(controls || collapsible) && (
            <HeadingControls>
              {controls}
              {collapsible && (
                <ExpandButton icon={expanded ? "angle-up" : "angle-down"} data-test="toggle-section-button" />
              )}
            </HeadingControls>
          )}
        </Heading>
        {children}
      </Collapse>
    </PropertiesPanelSectionContainer>
  );
};

const formatStrings = {
  title: {
    en: "Settings",
  },
} satisfies FormatMessages;
